export type HistoryItem = {
  cdkey: string;
  task_id?: string;
  status: string;
  display_status?: string;
  message?: string;
  accepted?: boolean;
  found?: boolean;
  error_code?: string;
  error_message?: string;
  updated_at: string;
};

export function extractItems(data: unknown) {
  if (!data || typeof data !== "object") return [];
  const root = data as Record<string, unknown>;
  const nested = root.data && typeof root.data === "object" ? (root.data as Record<string, unknown>) : undefined;
  const items = nested?.items || root.items;
  return Array.isArray(items) ? items : [];
}

function rowString(row: Record<string, unknown>, keys: string[]) {
  for (const key of keys) {
    const value = row[key];
    if (typeof value === "string" && value.trim()) return value.trim();
  }
  return "";
}

function rowBoolean(row: Record<string, unknown>, key: string) {
  return typeof row[key] === "boolean" ? (row[key] as boolean) : undefined;
}

function shouldKeepCurrentHistoryItem(current: HistoryItem, update: HistoryItem) {
  if (update.status === "not_found") return current.status !== "not_found";
  if (current.status === "success" && update.status !== "success") return true;
  if (current.status === "pending_dispatch" && update.status === "not_found") return true;
  return false;
}

function historyItemFromRow(row: Record<string, unknown>, now: string, cdkeyFallback = ""): HistoryItem | null {
  const cdkey = rowString(row, ["cdkey", "key"]) || cdkeyFallback;
  if (!cdkey.trim()) return null;
  const displayStatus = rowString(row, ["display_status"]);
  const status = rowString(row, ["status"]) || "submitted";
  const message = rowString(row, ["error_message", "reason", "message"]) || displayStatus || "已提交，等待系统处理";
  const item: HistoryItem = {
    cdkey: cdkey.trim(),
    task_id: rowString(row, ["task_id", "id", "taskId"]) || undefined,
    status,
    display_status: displayStatus || undefined,
    message,
    updated_at: now
  };
  const accepted = rowBoolean(row, "accepted");
  if (accepted !== undefined) item.accepted = accepted;
  const found = rowBoolean(row, "found");
  if (found !== undefined) item.found = found;
  const errorCode = rowString(row, ["error_code"]);
  if (errorCode) item.error_code = errorCode;
  const errorMessage = rowString(row, ["error_message"]);
  if (errorMessage) item.error_message = errorMessage;
  return item;
}

export function historyRowsFromResult(data: unknown, now = new Date().toISOString()): HistoryItem[] {
  return extractItems(data)
    .map((item) => {
      const row = item && typeof item === "object" ? (item as Record<string, unknown>) : {};
      return historyItemFromRow(row, now);
    })
    .filter((item): item is HistoryItem => Boolean(item));
}

export function mergeHistoryByCdkey(current: HistoryItem[], data: unknown, now = new Date().toISOString()) {
  const rows = historyRowsFromResult(data, now);
  if (!rows.length) return current;

  const updates = new Map(rows.map((row) => [row.cdkey.toLowerCase(), row]));
  return current.map((item) => {
    const update = updates.get(item.cdkey.toLowerCase());
    if (!update) return item;
    if (shouldKeepCurrentHistoryItem(item, update)) return item;
    return {
      ...item,
      ...update,
      task_id: update.task_id || item.task_id,
      updated_at: now
    };
  });
}

export function mergeHistoryByTaskId(current: HistoryItem[], data: unknown, now = new Date().toISOString()) {
  const updates = new Map<string, HistoryItem>();
  for (const item of extractItems(data)) {
    const row = item && typeof item === "object" ? (item as Record<string, unknown>) : {};
    const taskId = rowString(row, ["task_id", "id", "taskId"]);
    if (!taskId) continue;
    const currentItem = current.find((historyItem) => historyItem.task_id === taskId);
    const update = historyItemFromRow(row, now, currentItem?.cdkey);
    if (update) updates.set(taskId, update);
  }
  if (!updates.size) return current;

  return current.map((item) => {
    if (!item.task_id) return item;
    const update = updates.get(item.task_id);
    if (!update) return item;
    if (shouldKeepCurrentHistoryItem(item, update)) return item;
    return {
      ...item,
      ...update,
      cdkey: update.cdkey || item.cdkey,
      task_id: update.task_id || item.task_id,
      updated_at: now
    };
  });
}
